import React, { useEffect, useState, useMemo, useCallback } from 'react'
import Box from '@mui/material/Box'
import { editObject, getObjectsWithFilter, getObject, addObject } from '../../../api/apiClientCrud'
import useNavigate from '../../hooks/useNavigate'
import { useAuth } from '../../../context/AuthContext'
import { useLocation } from 'react-router-dom'
import Form from '../../form/Form'
import QuotePDF from './QuotePDF'
import { buildQuotePayload, convertDocumentAndUploadFile } from '../../../utils/documentManager'
import { useFetchEntities } from '../../hooks/useFetchEntities'
import useSaveEntity from '../../hooks/useSaveEntity'
import { getQuoteFields } from './QuoteFields'
import Toolbar from '../../toolbar/Toolbar'
import utc from 'dayjs/plugin/utc'
import dayjs from 'dayjs'
dayjs.extend(utc)

const ROUTES = {
  EDIT: '/quotes/edit',
  VIEW: '/quotes/view',
  ADD: '/quotes/add'
}

const EditQuote = () => {
  const history = useNavigate()
  const { user } = useAuth()
  const location = useLocation()
  const path = location.pathname
  const quoteId = location.state?.id

  const [isSubmit, setIsSubmit] = useState(false)
  const [quote, setQuote] = useState({ lines: [] })
  const [company, setCompany] = useState([])
  const [img, setImg] = useState('')
  const [settings, setSettings] = useState({})

  const toolbarOptions = useMemo(
    () => ({
      title: () => {
        return path.includes('/add') ? 'Nouveau Devis' : `Devis ${quote.quote_number}`
      }
    }),
    [quote, path]
  )

  const setLines = useCallback((newLines) => setQuote((prev) => ({ ...prev, lines: newLines })), [])

  const mode = useMemo(() => {
    if (path === ROUTES.EDIT) return 'edit'
    if (path === ROUTES.VIEW) return 'view'
    if (path === ROUTES.ADD) return 'add'
    return null
  }, [path])

  const quoteQuery = mode === 'edit' ? { key: 'quote', fn: getObject, args: ['quotes', quoteId] } : null

  const queries = [
    { key: 'settings', fn: getObjectsWithFilter, args: ['settings', { company_id: user.company_id }] },
    { key: 'company', fn: getObject, args: ['company', user.company_id] },
    quoteQuery
  ].filter(Boolean)

  const { data: fetched, loading: loadingEntities } = useFetchEntities(queries)

  const { saveEntity, loading } = useSaveEntity({
    entityName: 'quotes',
    mode,
    id: quoteId,
    user,
    settingsKey: 'quote_number',
    counterName: 'quotes_nb',
    updateCounter: true,
    addFn: addObject,
    editFn: editObject,
    contractLineName: 'Quotes',
    args: [quote],
    onSuccessRedirect: (id) => history(location.state?.history || ROUTES.VIEW, { state: { id } }),
    onSuccess: (res) => {
      setQuote(res.data)
      convertDocumentAndUploadFile(
        <QuotePDF quote_param={res.data} company_param={company} logo_param={img} settings={settings} />,
        `devis_${res.data.quote_number}`,
        user,
        'quote',
        'quotes',
        res.data
      )
      setIsSubmit(false)
    },
    onError: () => setIsSubmit(false),
    buildPayload: buildQuotePayload
  })

  const memoizedFields = useMemo(() => getQuoteFields(user, location, setLines), [user, location, setLines])

  const options = useMemo(
    () => ({
      fields: memoizedFields,
      data: quote
    }),
    [memoizedFields, quote]
  )

  useEffect(() => {
    if (!loadingEntities) {
      if (fetched.settings) setSettings(fetched.settings)
      if (fetched.company) {
        setCompany(fetched.company)
        const attach = fetched.company.attachments?.find((a) => a.type === 'logo')
        if (attach?.file?.data) {
          const blob = new Blob([new Uint8Array(attach.file.data)])
          const reader = new FileReader()
          reader.onloadend = () => {
            const base64 = reader.result?.toString().split(',')[1] ?? ''
            setImg(base64)
          }
          reader.readAsDataURL(blob)
        }
      }
      if (fetched.quote) setQuote(fetched.quote)
    }
  }, [fetched, loadingEntities])

  useEffect(() => {
    const quoteDateString = quote.quote_date

    if (quoteDateString) {
      const dateUTC = dayjs.utc(quoteDateString)

      if (!dateUTC.isValid()) {
        console.error('Invalid quote date provided:', quoteDateString)
        return
      }

      const validityDays = parseInt(settings.quote_validity) || 0
      const validityDate = dateUTC.add(validityDays, 'day')

      setQuote((prev) => ({
        ...prev,
        validity_date: validityDate.toISOString()
      }))
    } else {
      setQuote((prev) => ({ ...prev, validity_date: null }))
    }
  }, [quote.quote_date, settings.quote_validity])

  const totalAmount = useMemo(() => {
    return quote.lines.reduce((sum, line) => {
      const qty = parseFloat(line.quantity) || 0
      const price = parseFloat(line.unit_price) || 0
      return sum + qty * price
    }, 0)
  }, [quote.lines])

  useEffect(() => {
    setQuote((prev) => ({
      ...prev,
      total_amount_net: totalAmount
    }))
  }, [totalAmount])

  useEffect(() => {
    if (isSubmit) {
      const cleanedQuote = {
        ...quote,
        lines: (quote.lines || []).map((line) => ({
          ...line,
          priceBook_id: line.priceBook_id < -1 ? -1 : line.priceBook_id
        }))
      }
      saveEntity({ data: cleanedQuote })
    }
  }, [isSubmit])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        overflowY: 'auto',
        flex: 1
      }}
    >
      <Toolbar options={toolbarOptions} object={quote} />

      <Form options={options} mode='edit' setDataParent={setQuote} setIsSubmitParent={setIsSubmit} loading={loading} />
    </Box>
  )
}

export default EditQuote
